import { DataTable, DataTableRowEditCompleteEvent } from "primereact/datatable";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Column, ColumnEditorOptions, } from "primereact/column";
import { OtherUserModel } from "../../models/all-user/edit-user-model";
import { InputText } from "primereact/inputtext";
import React from "react";
import { Button } from "primereact/button";
import editUserServices from "../../services/edit-users/edit-users.service";
import { Toast, ToastMessage } from "primereact/toast";
import orderServices from "../../services/orders/orderservice";
import { InputSwitch } from "primereact/inputswitch";
import { Dialog } from "primereact/dialog";
import { Dropdown, DropdownFilterEvent } from "primereact/dropdown";

interface DataTableProps {
  data: OtherUserModel[];
}

const ReferralsDataTable: React.FC<DataTableProps> = (props) => {
  const { t } = useTranslation();
  const toast = useRef<Toast>(null);
  const [userData, setUserData] = useState<OtherUserModel[] | any>([]);

  const showMessage = (label: string, message: string, severity: ToastMessage['severity']) => {
    toast.current?.show({ severity, summary: label, detail: message, life: 3000 });
  };

  // ──────────────────────────────────────────────────────────────────────────
  // REFERRED BY DIALOG state
  // ──────────────────────────────────────────────────────────────────────────
  const [referrerDialog, setReferrerDialog] = useState(false);
  const [pendingReferrerRow, setPendingReferrerRow] = useState<OtherUserModel | null>(null);
  const [pendingReferrerValue, setPendingReferrerValue] = useState<number | null>(null);
  const [referrerSaving, setReferrerSaving] = useState(false);

  const blankUserDetail: any[] = [{ userID: 0, name: '', primaryContactNbr: '' }];
  const [orderUsers, setOrderUsers] = useState<any[]>(blankUserDetail);
  const [orderUser, setOrderUser] = useState<any[]>([]);

  useEffect(() => {
    setOrderUser(orderUsers.map((user: any) => ({
      userID: user.userID,
      detail: `${user.name}, ${user.primaryContactNbr}`,
    })));
  }, [orderUsers]);

  const handleDropdownFilter = (e: DropdownFilterEvent) => {
    if (e.filter && e.filter.length > 2) {
      orderServices.getOrderUsers(e.filter).then((response: any) => {
        if (response && response.length > 0) {
          setOrderUsers(response)
        }
      }).catch(err => console.log(err));
    }
  }

  const openReferrerDialog = (rowData: OtherUserModel) => {
    setPendingReferrerRow(rowData);
    setPendingReferrerValue(rowData.referredBy || null);

    // Seed initial option if exists so Dropdown holds it gracefully
    if (rowData.referredBy && rowData.referredByName) {
      if (!orderUser.find(u => u.userID === rowData.referredBy)) {
        setOrderUser([{ userID: rowData.referredBy, detail: `${rowData.referredByName}, (Current)` }, ...orderUser]);
      }
    }
    setReferrerDialog(true);
  };

  const submitReferrerChange = async () => {
    if (!pendingReferrerRow) return;

    setReferrerSaving(true);
    try {
      let putData = {
        ...pendingReferrerRow,
        referredBy: pendingReferrerValue
      };

      const response = await editUserServices.updateUser(pendingReferrerRow.userID, putData);

      if (response?.isSuccess) {
        let selectedReferrerName = '';
        if (pendingReferrerValue) {
          const referrerSelected = orderUser.find((u: any) => u.userID === pendingReferrerValue);
          if (referrerSelected && referrerSelected.detail) {
            selectedReferrerName = referrerSelected.detail.split(',')[0].trim();
          } else {
            selectedReferrerName = pendingReferrerRow.referredByName || '';
          }
        }

        setUserData((prev: OtherUserModel[]) =>
          prev.map((u: OtherUserModel) =>
            u.userID === pendingReferrerRow.userID
              ? { ...u, referredBy: pendingReferrerValue ?? 0, referredByName: selectedReferrerName }
              : u
          )
        );
        showMessage('Referrer Update', 'Referred By updated successfully', 'success');
        setReferrerDialog(false);
      } else {
        showMessage('Referrer Update', response?.message || 'Failed to update referrer', 'error');
      }
    } catch {
      showMessage('Referrer Update', 'Error updating referrer', 'error');
    } finally {
      setReferrerSaving(false);
    }
  };

  const referredByBodyTemplate = (rowData: any) => {
    const hasReferrer = !!rowData.referredByName;

    return (
      <div
        className={`flex align-items-center justify-content-between p-2 border-round transition-colors transition-duration-150 ${!hasReferrer ? 'surface-hover' : ''}`}
        style={{ cursor: hasReferrer ? 'default' : 'pointer', border: '1px dashed #d1d5db', minHeight: '38px', minWidth: '130px', background: hasReferrer ? '#f0fdf4' : '#f9fafb' }}
        onClick={() => !hasReferrer && openReferrerDialog(rowData)}
        title={hasReferrer ? "" : "Click to assign referrer"}
      >
        <span style={{ color: hasReferrer ? '#16a34a' : '#9ca3af', fontWeight: hasReferrer ? '600' : 'normal', fontSize: '0.85rem' }}>
          {rowData.referredByName || 'Assign Referrer...'}
        </span>
        {!hasReferrer && (
          <Button icon="pi pi-pencil" className="p-button-rounded p-button-text p-button-sm p-0 m-0" style={{ width: '1.5rem', height: '1.5rem', color: '#6b7280' }} />
        )}
      </div>
    );
  };

  const employeeBodyTemplate = (rowData: OtherUserModel) => {
    return (
      <div className="flex align-items-center justify-content-center gap-2">
        <InputSwitch
          checked={rowData.isEmployee === true}
          disabled
          style={{ opacity: 1 }}
        />
      </div>
    );
  };

  const employeeEditor = (options: ColumnEditorOptions) => {
    return (
      <div className="flex align-items-center justify-content-center gap-2">
        <InputSwitch
          checked={options.value === true}
          onChange={(e) => options.editorCallback!(e.value)}
        />
      </div>
    );
  };

  const directRewardBodyTemplate = (rowData: OtherUserModel) => {
    return (
      <div className="flex align-items-center justify-content-center gap-2">
        <InputSwitch
          checked={rowData.isDirectEarning === true}
          disabled
          style={{ opacity: 1 }}
        />
      </div>
    );
  };

  const directRewardEditor = (options: ColumnEditorOptions) => {
    return (
      <div className="flex align-items-center justify-content-center gap-2">
        <InputSwitch
          checked={options.value === true}
          onChange={(e) => options.editorCallback!(e.value)}
        />
      </div>
    );
  };

  useEffect(() => {
    setUserData(props.data);
  }, [props.data]);

  const onRowEditComplete = async (event: DataTableRowEditCompleteEvent) => {
    let _data = [...userData];
    let { newData, index } = event;
    if (_data[index] !== newData) {
      let putData = {
        ...newData
      };
      let response = await editUserServices.updateUser(newData.userID, putData);
      if (response.isSuccess) {
        showMessage('Update', 'Details updated successfully', 'success');
        _data[index] = newData as OtherUserModel;
        setUserData(_data);
      } else {
        showMessage('Update Error', response.message + ': Error in update', 'error');
      }
    }
  };

  const textEditor = (options: ColumnEditorOptions) => (
    <InputText type="text" value={options.value} style={{ width: '90%', border: '1px solid #090' }}
      onChange={(e: React.ChangeEvent<HTMLInputElement>) => options.editorCallback!(e.target.value)} />
  );

  return (
    <>
      <Toast ref={toast} position="top-center" />
      <div className="card border-noround p-2">
        <DataTable value={userData.length > 0 ? userData : props.data} editMode="row" dataKey="id" onRowEditComplete={onRowEditComplete} paginator
          rows={10} rowsPerPageOptions={[5, 10, 25, 50, 100]} paginatorTemplate="RowsPerPageDropdown FirstPageLink PrevPageLink CurrentPageReport NextPageLink LastPageLink"
          currentPageReportTemplate="{first} to {last} of {totalRecords}">
          <Column 
            field="name" 
            header="User Name" 
            style={{ width: '25%' }} 
            body={(rowData) => <span className="user-name-cell">{rowData.name}</span>}
          />
          <Column 
            field="primaryContactNbr" 
            header="Primary Contact" 
            style={{ width: '20%' }} 
            body={(rowData) => <span className="contact-cell"><i className="pi pi-phone mr-2" style={{fontSize: '0.8rem'}}></i>{rowData.primaryContactNbr}</span>}
          />
          <Column field="referredBy" header="Referred By" body={referredByBodyTemplate} style={{ width: '25%' }} />
          <Column
            field="isEmployee"
            header="Reward Earning Restriction"
            body={employeeBodyTemplate}
            editor={(options) => employeeEditor(options)}
            style={{ width: "15%", textAlign: "center" }}
            bodyStyle={{ textAlign: 'center' }}
          />
          <Column
            field="isDirectEarning"
            header="Direct Reward Earning"
            body={directRewardBodyTemplate}
            editor={(options) => directRewardEditor(options)}
            style={{ width: "15%", textAlign: "center" }}
            bodyStyle={{ textAlign: 'center' }}
          />
          <Column rowEditor header="Actions" headerStyle={{ minWidth: '3em', textAlign: 'right' }} bodyStyle={{ textAlign: 'right' }} />
        </DataTable>
      </div>

      <Dialog
        header="Assign Referrer"
        visible={referrerDialog}
        style={{ width: '30vw' }}
        modal
        onHide={() => setReferrerDialog(false)}
        footer={
          <div className="flex justify-content-end gap-2">
            <Button label="Cancel" className="p-button-secondary p-button-text" onClick={() => setReferrerDialog(false)} disabled={referrerSaving} />
            <Button 
              label="Save" 
              icon={referrerSaving ? "pi pi-spin pi-spinner" : "pi pi-check"} 
              onClick={submitReferrerChange} 
              disabled={referrerSaving || (pendingReferrerValue === pendingReferrerRow?.userID && pendingReferrerValue !== null)} 
            />
          </div>
        }
      >
        <div className="flex flex-column gap-3 pt-3">
          <div className="flex align-items-center gap-2">
            <i className="pi pi-user text-600"></i>
            <span className="font-semibold text-700">Customer:</span>
            <span>{pendingReferrerRow?.name}</span>
          </div>
          <div className="p-field flex flex-column gap-2 mt-2">
            <label htmlFor="referrerSelect" className="font-semibold text-700">Select Referrer <span className="text-red-500">*</span></label>
            <Dropdown
              id="referrerSelect"
              value={pendingReferrerValue}
              options={orderUser}
              onChange={(e) => setPendingReferrerValue(e.value)}
              optionLabel="detail"
              optionValue="userID"
              filter
              showClear
              onFilter={handleDropdownFilter}
              placeholder="Search by Mobile or Name"
              className={`w-full ${pendingReferrerValue === pendingReferrerRow?.userID && pendingReferrerValue !== null ? 'p-invalid' : ''}`}
            />
            {pendingReferrerValue === pendingReferrerRow?.userID && pendingReferrerValue !== null && (
              <small className="p-error block mt-1 font-semibold">
                <i className="pi pi-exclamation-circle mr-1" style={{ fontSize: '0.8rem' }}></i>
                A user cannot be assigned as their own referrer.
              </small>
            )}
            <small className="text-500">Must type at least 3 characters to search</small>
          </div>
        </div>
      </Dialog>
    </>
  );
};
export default ReferralsDataTable;
